// TaskCard.jsx
import { useUser } from "@clerk/clerk-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTaskContext } from "../../providers/TaskProvider";
import {
  insertTaskAttachments,
  runMigration,
  updateTask,
  uploadMultipleFiles,
} from "../../services/supabase";
import { formatShortcut } from "../../utils/keyboardShortcuts";
import TaskAttachments from "../molecules/TaskAttachments";
import TaskCardActions from "../molecules/TaskCardActions";
import TaskCardBody from "../molecules/TaskCardBody";
import TaskCardFooter from "../molecules/TaskCardFooter";
import TaskCardHeader from "../molecules/TaskCardHeader";

/**
 * TaskCard - Draggable task card component with editing capabilities
 *
 * @param {Object} props
 * @param {Object} props.task - Task data
 * @param {Array} props.users - Available users
 */
export default function TaskCard({ task, users }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState({ ...task });
  const [fileUploads, setFileUploads] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationError, setMigrationError] = useState(null);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [attachmentsKey, setAttachmentsKey] = useState(Date.now());
  const [isHovered, setIsHovered] = useState(false);

  const titleRef = useRef(null);
  const fileInputRef = useRef(null);

  const { user } = useUser();
  const { deleteTask, refreshTasks } = useTaskContext();

  // Optimize sortable configuration with useMemo
  const sortableConfig = useMemo(
    () => ({
      id: task.id,
      data: {
        type: "task",
        task,
        status: task.status,
      },
      disabled: isEditing,
    }),
    [task.id, task, task.status, isEditing]
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable(sortableConfig);

  // Detect touch device on mount
  useEffect(() => {
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  // Calculate style for dragging - memoized for performance
  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? "none" : transition,
      opacity: isDragging ? 0.65 : 1,
      zIndex: isDragging ? 1000 : 1,
      position: isDragging ? "relative" : "static",
      boxShadow: isDragging
        ? "0 0 20px rgba(77, 177, 224, 0.4)"
        : isHovered
          ? "0 0 10px rgba(77, 177, 224, 0.3)"
          : "none",
    }),
    [transform, transition, isDragging, isHovered]
  );

  // Handle mouse enter/leave for hover effects
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  // Update edited task when task changes
  useEffect(() => {
    if (isEditing && titleRef.current) {
      titleRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditedTask({ ...task });
  }, [task]);

  // Check if this task has attachments in the new system
  const checkForAttachments = useCallback(async () => {
    // This will be handled by the TaskAttachments component
    // We just need a way to communicate back up
  }, [task.id]);

  // Callback to receive attachment status from TaskAttachments component
  const handleAttachmentsLoaded = useCallback((attachmentsCount) => {
    setHasAttachments(attachmentsCount > 0);
  }, []);

  // Memoize handlers for better performance
  const handleEdit = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
        if (e.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
        }
      }
      if (!isDragging) {
        setIsEditing(true);
      }
    },
    [isDragging]
  );

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditedTask({ ...task });
    setFileUploads([]);
  }, [task]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditedTask((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDateChange = useCallback((e) => {
    setEditedTask((prev) => ({ ...prev, due_date: e.target.value }));
  }, []);

  const handleAssigneeChange = useCallback((e) => {
    setEditedTask((prev) => ({ ...prev, assignee_id: e.target.value }));
  }, []);

  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileUploads(Array.from(e.target.files));
    }
  }, []);

  const handleDelete = async (e) => {
    if (e) {
      e.stopPropagation();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }

    if (
      window.confirm(
        `Are you sure you want to delete the task "${task.title}"?`
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteTask(task.id);
      } catch (error) {
        console.error("Failed to delete task:", error);
        window.alert(
          `Error deleting task: ${error.message || "Unknown error"}`
        );
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSave = async () => {
    // Start with updates from edited task
    const updates = { ...editedTask };

    // Update task in Supabase
    const updatedTask = await updateTask(task.id, updates);

    if (updatedTask) {
      // Handle file uploads if there are new files
      if (fileUploads.length > 0) {
        setIsUploading(true);

        // If multiple files are being uploaded, try to run the migration first
        if (fileUploads.length > 1) {
          try {
            setIsMigrating(true);
            await runMigration();
            setIsMigrating(false);
          } catch (error) {
            console.error("Migration failed, will use fallback method:", error);
            setMigrationError(error.message);
          }
        }

        const uploadedFileUrls = await uploadMultipleFiles(fileUploads);

        if (uploadedFileUrls.length > 0) {
          // Map URLs to file objects with names
          const fileObjects = uploadedFileUrls.map((url, index) => ({
            url,
            name: fileUploads[index].name,
          }));

          // Insert attachments into task_attachments table
          await insertTaskAttachments(task.id, fileObjects);

          // Force refresh of attachments
          setAttachmentsKey(Date.now());
        }

        setIsUploading(false);
      }

      // Exit edit mode
      setIsEditing(false);
      setFileUploads([]);

      // Refresh tasks to ensure UI is updated
      refreshTasks();
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (isEditing) {
      // Escape key cancels editing
      if (e.key === "Escape") {
        handleCancel();
      }
      // Ctrl+Enter or Cmd+Enter saves changes
      else if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        handleSave();
      }
    }
  };

  // Handle touch interactions for mobile
  const handleTouchStart = () => {
    setTouchStartTime(Date.now());
  };

  const handleTouchEnd = (e) => {
    const touchDuration = Date.now() - touchStartTime;
    if (touchDuration > 500 && !isEditing) {
      handleEdit(e);
    }
  };

  // Handle button click to prevent drag
  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  // Format the filename for display (truncate if too long)
  const formatFileName = (fileName) => {
    if (!fileName) return "";
    if (fileName.length <= 15) return fileName;
    const ext = fileName.split(".").pop();
    const name = fileName.substring(0, fileName.length - ext.length - 1);
    return `${name.substring(0, 10)}...${ext}`;
  };

  // Find assignee name from users array
  const getAssigneeName = (id) => {
    if (!id) return "Unassigned";
    const assignee = users?.find((u) => u.id === id);
    return assignee ? assignee.firstName + " " + assignee.lastName : "Unknown";
  };

  // Get badge color based on status
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "Incoming":
        return "primary";
      case "Todo":
        return "neutral";
      case "In Progress":
        return "success";
      case "Ideation":
        return "warning";
      case "Done":
        return "success";
      default:
        return "neutral";
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-[#1a1e21] p-3 rounded-md shadow-md mb-2 border ${
        isEditing
          ? "border-[#4db1e0]"
          : isHovered
            ? "border-[#4db1e0]/50"
            : "border-[#2f3236]"
      } transition-all duration-200`}
      onKeyDown={handleKeyDown}
      onTouchStart={isTouchDevice ? handleTouchStart : undefined}
      onTouchEnd={isTouchDevice ? handleTouchEnd : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={!isEditing ? handleEdit : undefined}
      tabIndex={0}
      aria-label={`Task: ${task.title}`}
      data-shortcut={formatShortcut("e", "Edit task")}
    >
      <TaskCardHeader
        task={task}
        isEditing={isEditing}
        onEdit={handleEdit}
        onChange={handleChange}
        editedTask={editedTask}
        titleRef={titleRef}
      />

      <TaskCardBody
        task={task}
        isEditing={isEditing}
        onChange={handleChange}
        editedTask={editedTask}
        users={users}
        onDateChange={handleDateChange}
        onAssigneeChange={handleAssigneeChange}
      />

      <TaskAttachments
        taskId={task.id}
        isEditing={isEditing}
        key={attachmentsKey}
        onAttachmentsLoaded={handleAttachmentsLoaded}
      />

      <TaskCardFooter
        task={task}
        isEditing={isEditing}
        hasAttachments={hasAttachments}
        fileUploads={fileUploads}
        onFileChange={handleFileChange}
        fileInputRef={fileInputRef}
      />

      <TaskCardActions
        isEditing={isEditing}
        isDeleting={isDeleting}
        isUploading={isUploading}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />
    </div>
  );
}
